import { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { TreasurySheet } from './TreasurySheet';
import { KPI } from '@heroui-pro/react/kpi';
import {
  CalendarDays,
  FileClock,
  Wrench,
  ShieldAlert,
  MessageSquareWarning,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { computeKpis, type Kpi } from '@odd/domain';
import { useBoardCtx } from './BoardContext';
import { useErpLinks } from '../lib/erp';
import { ErpLink } from './ui';
import { CompactPulse } from '../v2/CompactPulse';

const ICON: Record<Kpi['id'], LucideIcon> = {
  K1: CalendarDays,
  K2: FileClock,
  K3: Wrench,
  K4: ShieldAlert,
  K5: MessageSquareWarning,
  K6: Wallet,
};
const STATUS = { rojo: 'danger', ambar: 'warning', verde: 'success' } as const;
const WORD = { rojo: 'en rojo', ambar: 'en ámbar', verde: 'en objetivo' } as const;

/** Pulso de la oficina: 6 KPI de HeroUI Pro. Más alertas = peor, así que la tendencia al alza va en rojo. */
export function Pulse({ compact = false }: { compact?: boolean } = {}) {
  const { board, rules, filter } = useBoardCtx();
  const [treasuryOpen, setTreasuryOpen] = useState(false);
  const erp = useErpLinks();
  const hidden = new Set(board.display?.hiddenKpis ?? []);
  const kpis = computeKpis({
    alerts: board.alerts,
    previousAlerts: board.previousSnapshot ? board.previousAlerts : null,
    meetings: board.upcomingMeetings,
    previousMeetings: board.previousSnapshot ? board.previousUpcomingMeetings : null,
    rules,
    filter,
  }).filter((k) => !hidden.has(k.id));
  // Mismo cálculo sin filtro de segmento: si A+B esconde casos, la tarjeta lo dice
  const allSegments =
    filter.segment === 'AB'
      ? computeKpis({
          alerts: board.alerts,
          previousAlerts: null,
          meetings: board.upcomingMeetings,
          previousMeetings: null,
          rules,
          filter: { ...filter, segment: 'ALL' },
        })
      : null;

  if (kpis.length === 0) return null;
  // Las columnas se ajustan a las tarjetas visibles (clases estáticas para Tailwind)
  const XL = [
    '',
    'xl:grid-cols-1',
    'xl:grid-cols-2',
    'xl:grid-cols-3',
    'xl:grid-cols-4',
    'xl:grid-cols-5',
    'xl:grid-cols-6',
  ];
  return (
    <>
      {compact ? (
        <CompactPulse
          kpis={kpis}
          allSegments={allSegments}
          icons={ICON}
          pendingMinutesUrl={erp.pendingMinutes()}
          onTreasury={() => setTreasuryOpen(true)}
        />
      ) : (
        <section
          aria-label="Pulso de la oficina"
          className={`grid grid-cols-2 gap-3 md:grid-cols-3 ${XL[kpis.length]}`}
        >
          {kpis.map((k) => {
            const Icon = ICON[k.id];
            if (!k.available)
              return (
                <KPI key={k.id} className="opacity-60">
                  <KPI.Header>
                    <KPI.Icon>
                      <Icon className="size-4" />
                    </KPI.Icon>
                    <KPI.Title>{k.label}</KPI.Title>
                  </KPI.Header>
                  <KPI.Content>
                    <span className="text-3xl font-extrabold text-ink-3">—</span>
                  </KPI.Content>
                  <KPI.Footer>
                    <span className="text-xs text-ink-3">Sin datos todavía · {k.sourceLabel}</span>
                  </KPI.Footer>
                </KPI>
              );
            const red = Number(k.sub.split(' ')[0]) || 0;
            const total = allSegments?.find((x) => x.id === k.id)?.value ?? k.value;
            const redPct = k.id === 'K1' ? 0 : k.value ? Math.round((red / k.value) * 100) : 0;
            // K6 abre el detalle de tesorería (saldos de Gesfincas de todas las comunidades)
            const clickable = k.id === 'K6';
            const open = () => setTreasuryOpen(true);
            return (
              <KPI
                key={k.id}
                {...(clickable && {
                  role: 'button',
                  tabIndex: 0,
                  'aria-label': `${k.label}: ver detalle`,
                  onClick: open,
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      open();
                    }
                  },
                  className:
                    'cursor-pointer transition-shadow hover:shadow-md hover:ring-1 hover:ring-orange/50 focus-visible:ring-2 focus-visible:ring-orange',
                })}
              >
                <KPI.Header>
                  <KPI.Icon status={STATUS[k.light]}>
                    <Icon className="size-4" />
                  </KPI.Icon>
                  <KPI.Title>{k.label}</KPI.Title>
                </KPI.Header>
                <KPI.Content>
                  <KPI.Value value={k.value} className="text-3xl font-extrabold" />
                  {k.delta != null && k.delta !== 0 && (
                    <KPI.Trend
                      trend={k.delta > 0 ? 'up' : 'down'}
                      {...({ color: k.delta > 0 ? 'danger' : 'success' } as object)}
                    >
                      {Math.abs(k.delta)}
                    </KPI.Trend>
                  )}
                </KPI.Content>
                {k.id !== 'K1' && <KPI.Progress value={redPct} status={redPct > 50 ? 'danger' : 'warning'} />}
                <KPI.Footer>
                  <div className="grid gap-0.5">
                    <span className="text-xs text-ink-2">
                      {k.id === 'K1' ? k.sub : `${red} ${WORD.rojo}`}
                      {k.delta === 0 && ' · igual que la semana pasada'}
                    </span>
                    {total !== k.value && (
                      <span className="text-xs font-semibold text-orange-ink">
                        {total} con todos los segmentos
                      </span>
                    )}
                    {clickable && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-ink">
                        Ver detalle de tesorería <ArrowRight className="size-3.5" />
                      </span>
                    )}
                    {k.id === 'K2' && (
                      <ErpLink
                        href={erp.pendingMinutes()}
                        title="Juntas con el acta pendiente en Eleva"
                        className="text-xs font-semibold"
                      >
                        Ver actas pendientes en Eleva
                      </ErpLink>
                    )}
                  </div>
                </KPI.Footer>
              </KPI>
            );
          })}
        </section>
      )}
      <TreasurySheet isOpen={treasuryOpen} onClose={() => setTreasuryOpen(false)} />
    </>
  );
}
